// UserIcon — ikona přihlášeného uživatele.
//
// variant="topbar"  (výchozí) — řádek: avatar + jméno, klik → dropdown
// variant="sidebar"           — blok do spodní části sidebaru
// variant="compact"           — jen avatar (kolečko), pro umístění vedle jiných prvků v řádku
//
// Nepřihlášený: zobrazí "Anonymní uživatel" + možnost přihlásit se.
// Přihlášený:   zobrazí jméno + možnost odhlásit se.
// Po načtení stránky ověří token přes GET /api/auth/me.
import { useEffect, useRef, useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/useAuthStore'
import { useDeployConfigStore } from '../../store/useDeployConfigStore'
import { useLoginModalStore } from '../../store/useLoginModalStore'
import { useVerifyToken } from '../../hooks/useVerifyToken'
import { trackView } from '../../hooks/useTrackProduct'
import { trackFlush } from '../../api/usageApi'

function initials(user) {
  if (!user) return '?'
  if (user.display_name) return user.display_name.slice(0, 2).toUpperCase()
  return ((user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')).toUpperCase() || user.username.slice(0, 2).toUpperCase()
}

function displayName(user) {
  if (!user) return 'Anonymní uživatel'
  return user.display_name || `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.username
}

// ─── Dropdown ──────────────────────────────────────────────────────────────────
// Vykresluje se portálem do <body> a pozicuje se podle anchorRef, aby ho neořezávaly
// overflow-hidden kontejnery (sidebar) ani nezakryly vyšší z-indexy (Leaflet mapa).
function Dropdown({ user, onLogin, onLogout, onClose, anchorRef, placement = 'bottom-left' }) {
  const ref = useRef(null)
  const navigate = useNavigate()
  const [pos, setPos] = useState(null)
  const registrationEnabled = useDeployConfigStore(s => s.registrationEnabled)

  useLayoutEffect(() => {
    if (!anchorRef?.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const style = { position: 'fixed', zIndex: 1000 }
    if (placement === 'bottom-right') {
      style.top = rect.bottom + 4
      style.right = window.innerWidth - rect.right
    } else if (placement === 'top-stretch') {
      style.bottom = window.innerHeight - rect.top + 4
      style.left = rect.left
      style.width = rect.width
    } else {
      style.top = rect.bottom + 4
      style.left = rect.left
    }
    setPos(style)
  }, [anchorRef, placement])

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!pos) return null

  return createPortal(
    <div ref={ref} style={pos} className="bg-white border border-gray-200 rounded-xl shadow-lg py-2 min-w-48">
      {user ? (
        <>
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-800">{displayName(user)}</p>
            <p className="text-xs text-gray-400">{user.username}</p>
          </div>
          <button
            onClick={onLogin}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Změnit uživatele
          </button>
          <button
            onClick={onLogout}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            Odhlásit se
          </button>
        </>
      ) : (
        <>
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm text-gray-500">Přistupujete jako</p>
            <p className="text-sm font-semibold text-gray-700">Anonymní uživatel</p>
          </div>
          <button
            onClick={onLogin}
            className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
          >
            Přihlásit se
          </button>
          {registrationEnabled && (
            <button
              onClick={() => { onClose(); navigate('/register') }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Zaregistrovat se
            </button>
          )}
        </>
      )}
    </div>,
    document.body
  )
}

// Sdílené chování všech variant — dropdown open state + akce login/logout.
function useUserMenu() {
  useVerifyToken()
  const { user, logout } = useAuthStore()
  const showLogin = useLoginModalStore(s => s.show)
  const [showDropdown, setShowDropdown] = useState(false)
  const anchorRef = useRef(null)
  return {
    user, anchorRef, showDropdown, setShowDropdown,
    onClose:  () => setShowDropdown(false),
    onLogin:  () => { setShowDropdown(false); showLogin() },
    // Sledování přístupů (etapa K2): událost se zařadí a odešle JEŠTĚ PŘED logout() —
    // po něm už je token z localStorage pryč a dávka by se serveru přihlásila jako anonym.
    onLogout: () => { trackView('auth', 'logout'); trackFlush(); logout(); setShowDropdown(false) },
  }
}

// ─── Topbar varianta ───────────────────────────────────────────────────────────
function TopbarVariant() {
  const m = useUserMenu()
  return (
    <div className="relative" ref={m.anchorRef}>
      <button
        onClick={() => m.setShowDropdown(v => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
      >
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${m.user ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
          {m.user ? initials(m.user) : '?'}
        </div>
        <span className={`text-sm max-w-36 truncate ${m.user ? 'text-gray-800 font-medium' : 'text-gray-400 italic'}`}>
          {displayName(m.user)}
        </span>
      </button>
      {m.showDropdown && (
        <Dropdown user={m.user} anchorRef={m.anchorRef} onClose={m.onClose} onLogin={m.onLogin} onLogout={m.onLogout} />
      )}
    </div>
  )
}

// ─── Sidebar varianta ──────────────────────────────────────────────────────────
function SidebarVariant() {
  const m = useUserMenu()
  return (
    <div className="relative border-t border-gray-100" ref={m.anchorRef}>
      <button
        onClick={() => m.setShowDropdown(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${m.user ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
          {m.user ? initials(m.user) : '?'}
        </div>
        <div className="flex flex-col items-start min-w-0">
          <span className={`text-sm truncate max-w-full ${m.user ? 'font-medium text-gray-800' : 'italic text-gray-400'}`}>
            {displayName(m.user)}
          </span>
          {!m.user && <span className="text-xs text-gray-400">Klikněte pro přihlášení</span>}
        </div>
      </button>
      {m.showDropdown && (
        <Dropdown user={m.user} anchorRef={m.anchorRef} placement="top-stretch" onClose={m.onClose} onLogin={m.onLogin} onLogout={m.onLogout} />
      )}
    </div>
  )
}

// ─── Compact varianta ──────────────────────────────────────────────────────────
// placement default "bottom-right" — vyhovuje ikoně vpravo v sidebaru (StationSelectLayout/
// StationGroupLayout); pro ikonu v levém rohu (MeteogramLayout) je třeba "bottom-left", jinak se
// dropdown rozevře doleva mimo obrazovku.
function CompactVariant({ placement = 'bottom-right' }) {
  const m = useUserMenu()
  return (
    <div className="relative shrink-0" ref={m.anchorRef}>
      <button
        onClick={() => m.setShowDropdown(v => !v)}
        title={displayName(m.user)}
        className="flex items-center justify-center rounded-full hover:opacity-80 transition-opacity"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${m.user ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
          {m.user ? initials(m.user) : '?'}
        </div>
      </button>
      {m.showDropdown && (
        <Dropdown user={m.user} anchorRef={m.anchorRef} placement={placement} onClose={m.onClose} onLogin={m.onLogin} onLogout={m.onLogout} />
      )}
    </div>
  )
}

// ─── Export ────────────────────────────────────────────────────────────────────
// placement: jen pro variant="compact" — kam se má dropdown rozevřít vůči ikoně (viz CompactVariant).
export function UserIcon({ variant = 'topbar', placement }) {
  if (variant === 'sidebar') return <SidebarVariant />
  if (variant === 'compact') return <CompactVariant placement={placement} />
  return <TopbarVariant />
}
